import axios from "axios";
import { useEnvVariable } from "@/composables";
import { useCookies } from "@/composables";
import { tokenKey } from "@/enums";
import { useDefaultStore } from "@/shared/stores/default";
import { toast } from "@/shared/utils";

const { getEnvVar } = useEnvVariable();

function createApi() {
  const apiVersion = "v1";
  const cookies = useCookies();

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const apiClient = axios.create({
    baseURL: `${getEnvVar("HOST_ENDPOINT")}/api/${apiVersion}`,
    headers,
  });

  // Request interceptor
  apiClient.interceptors.request.use(
    (config) => {
      changeLoading();

      if (cookies.get(tokenKey)) {
        config.headers["Authorization"] = `Bearer ${cookies.get(tokenKey)}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  apiClient.interceptors.response.use(
    ({ data }) => {
      changeLoading(false);

      return data;
    },
    (error) => {
      changeLoading(false);

      const statusCode = error.response?.status;
      let toastError = error.response?.data?.message;

      // Сетевая ошибка / таймаут / CORS — response отсутствует. Раньше reject уходил с undefined,
      // что роняло потребителей на деструктуризации `catch ({ errors })` и `err.errors`.
      if (!error.response) {
        const networkMessage = "Нет соединения с сервером. Проверьте подключение";
        console.error(networkMessage, error);
        toast("error", { title: "Ошибка", message: networkMessage });
        return Promise.reject({ message: networkMessage, errors: {} });
      }

      if (statusCode === 401) {
        toastError = "Требуется авторизация для запроса";
        console.error(toastError, error);
        cookies.remove(tokenKey);
        location.reload();
      } else if (statusCode === 403) {
        console.error("Доступ запрещен", toastError || "Нет прав для выполнения операции");
      } else if (statusCode === 404) {
        console.error("Ресурс не найден", toastError);
      } else if (statusCode === 405) {
        toastError = error.response?.data?.message;
        console.error(toastError);
      } else if (statusCode === 422) {
        console.error("Ошибка валидации", toastError);
      } else if (statusCode === 500) {
        console.error("Ошибка сервера", toastError || "Внутренняя ошибка сервера");
      }

      // Стутусы ошибок, когда не нужно показывать тосты
      const exceptionStatuses = [403];

      if (!exceptionStatuses.includes(statusCode)) {
        toast("error", {
          title: "Ошибка",
          message: toastError,
        });
      }

      // Pass error to the caller
      return Promise.reject(error.response?.data);
    }
  );

  return apiClient;
}

function changeLoading(increase = true) {
  const defaultStore = useDefaultStore();
  defaultStore.changeLoading(increase);
}

export const apiClient = createApi();
